import { css } from '@emotion/react';
import { setLocalStorageItem } from '@revenuemarket/browser';
import {
  Button,
  PrimaryColors,
  Flex,
  Text,
  Colors,
  IconCheckCircleFill,
  CloseLine,
  KeyboardArrowRight,
} from '@revenuemarket/cds';
import { overlays } from '@revenuemarket/overlays';
import dayjs from 'dayjs';
import { CompanyUserPermission } from '~apis/role/type';
import { OnboardingStep } from '~apis/settings';
import { PermissionGuard } from '~components/Role/PermissionGuard';
import CertificateRegisterModal from '~components/ui/Modal/CertificateRegisterModal';
import InviteMemberModal from '~components/ui/Modal/InviteMemberModal';
import { useCompany } from '~hooks/queries/companies/query';
import { useGetOnboardingStatus, useGetSettings } from '~hooks/queries/setting/query';
import { useCompanyId } from '~hooks/useCompanyId';
import useIsDemoTrial from '~hooks/useIsDemoTrial';

const StatusText = ({
  finished,
  text,
  onClick,
}: {
  finished: boolean;
  text: string;
  onClick?: () => void;
}) => {
  return (
    <Flex
      align="center"
      justify="space-between"
      css={css`
        cursor: pointer;
        .arrow-icon {
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        &:hover .arrow-icon {
          opacity: 1;
        }
      `}
      onClick={onClick}
    >
      <Flex align="center" gap={2} style={{ cursor: 'pointer' }}>
        <IconCheckCircleFill
          width={20}
          height={20}
          fill={finished ? PrimaryColors.primary600 : Colors.gray200}
        />
        <Text textStyle="13_400" style={{ color: finished ? Colors.gray400 : Colors.gray800 }}>
          {text}
        </Text>
      </Flex>
      <KeyboardArrowRight className="arrow-icon" width={20} height={20} fill={Colors.gray800} />
    </Flex>
  );
};

const CLOBE_START_SHOW_BASE_TIME = dayjs('2025-07-25 18:00:00');

const ClobeStartInfo = ({ setShowStartInfo }: { setShowStartInfo: (value: boolean) => void }) => {
  const { data } = useGetOnboardingStatus();
  const { companyId } = useCompanyId();
  const settings = useGetSettings();
  const isDemoTrial = useIsDemoTrial();

  const step = settings.data?.Data?.data?.onboardingStep;

  const { data: company } = useCompany({ companyId });

  const companyCreatedAt = company?.Data?.data?.createdAt;

  const applyTarget =
    companyCreatedAt && dayjs(companyCreatedAt).isAfter(CLOBE_START_SHOW_BASE_TIME);

  const onboardingStatus = data?.Data?.data;
  const showClose =
    onboardingStatus?.bankAccountRegistered && onboardingStatus?.ntsCertificateRegistered;

  // 관련 히스토리 : https://clobe-ai.slack.com/archives/C090UDFSMNC/p1753164523358409?thread_ts=1753163042.738569&channel=C090UDFSMNC&message_ts=1753164523.358409
  // 기존 워크스페이스: 초기설정 미완료 워크스페이스
  // 신규 워크스페이스: 모든 신규 워크스페이스
  const allFinished = applyTarget
    ? onboardingStatus?.bankAccountRegistered &&
      onboardingStatus?.ntsCertificateRegistered &&
      onboardingStatus?.hasInvitation
    : step && OnboardingStep[step] === OnboardingStep.ONBOARDED;

  if (allFinished || !data || isDemoTrial) {
    return null;
  }

  return (
    <div
      style={{
        padding: '8px 0px',
      }}
    >
      <Flex
        direction="column"
        style={{
          padding: 12,
          width: '100%',
          borderRadius: 12,
          background: Colors.white,
          border: `1px solid ${PrimaryColors.primary600}`,
          boxShadow: '0px 8px 16px 0px rgba(29, 34, 37, 0.02)',
        }}
        gap={8}
      >
        <Flex align="center" justify="space-between">
          <Text
            textStyle="13_600"
            style={{
              color: Colors.gray800,
            }}
          >
            시작하기
          </Text>

          {showClose && (
            <Button
              buttonType="transparent"
              buttonSize="small"
              onClick={() => {
                setShowStartInfo(false);
                setLocalStorageItem(`${companyId}_clobe_start_info_hide`, true);
              }}
            >
              <CloseLine />
            </Button>
          )}
        </Flex>
        <StatusText
          finished={Boolean(onboardingStatus?.bankAccountRegistered)}
          text="은행 연동"
          onClick={() => {
            overlays.open(({ isOpen, onClose }) => (
              <CertificateRegisterModal open={isOpen} onCancel={onClose} />
            ));
          }}
        />
        <StatusText
          finished={Boolean(onboardingStatus?.ntsCertificateRegistered)}
          text="홈택스 연동"
          onClick={() => {
            overlays.open(({ isOpen, onClose }) => (
              <CertificateRegisterModal open={isOpen} onCancel={onClose} />
            ));
          }}
        />
        <PermissionGuard required={CompanyUserPermission.CAN_MANAGE_COMPANY_USERS}>
          <StatusText
            finished={Boolean(onboardingStatus?.hasInvitation)}
            text="구성원 초대"
            onClick={() => {
              overlays.open(({ isOpen, onClose }) => {
                return (
                  <InviteMemberModal
                    open={isOpen}
                    onCancel={() => {
                      onClose();
                    }}
                  />
                );
              });
            }}
          />
        </PermissionGuard>
      </Flex>
    </div>
  );
};

export default ClobeStartInfo;
